import { ChangeEvent, useState, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ThemingFormPanelValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import {
    AXNUIContent,
    AXNUIContentFormPanel,
    AXNUILayout,
    AXNUILayoutFormPanel,
    AXNUITheme,
    AXNUIThemeFormPanel,
    AXNUIThemeGeneral
} from "@iddataweb/idw-common-ui/types";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { ColorPickerFormGroup } from "../../../../common/color-picker-form-group/color-picker-form-group";
import { TextareaFormGroup } from "../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormPanelTextSection } from "./theming-form-panel-text-section/theming-form-panel-text-section";
import { ThemingFormPanelLogoSection } from "./theming-form-panel-logo-section/theming-form-panel-logo-section";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingFormPanelTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ThemingFormPanelValidationErrors;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUILayout: AXNUILayout;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUILayoutPropertyValue: (axnuiLayout: AXNUILayout) => void;
    updateAXNUIThemePropertyValue: (axnuiTheme: AXNUITheme) => void;
};

export const ThemingFormPanelTabContainer = (props: ThemingFormPanelTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        axnuiContent,
        axnuiLayout,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUILayout,
        inheritedAXNUITheme,
        pageLevel,
        generatePreviewComponent,
        uploadFileToS3,
        updateAXNUIContentPropertyValue,
        updateAXNUILayoutPropertyValue,
        updateAXNUIThemePropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIThemeGeneralFieldValue = (key: keyof AXNUIThemeGeneral, value: string) => {
        const newPropertyValue: AXNUITheme = {
            ...axnuiTheme,
            general: {
                ...axnuiTheme.general,
                [key]: value
            }
        };
        updateAXNUIThemePropertyValue(newPropertyValue);
    };

    const updateAXNUIContentFormPanelFieldValue = (key: keyof AXNUIContentFormPanel, value: string) => {
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            formPanel: {
                ...axnuiContent.formPanel,
                [key]: value
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const updateAXNUILayoutFormPanelFieldValue = (key: keyof AXNUILayoutFormPanel, value: string | boolean) => {
        const newPropertyValue: AXNUILayout = {
            ...axnuiLayout,
            formPanel: {
                ...axnuiLayout.formPanel,
                [key]: value
            }
        };
        updateAXNUILayoutPropertyValue(newPropertyValue);
    };

    const updateAXNUIThemeFormPanelFieldValue = (key: keyof AXNUIThemeFormPanel, value: string) => {
        const newPropertyValue: AXNUITheme = {
            ...axnuiTheme,
            formPanel: {
                ...axnuiTheme.formPanel,
                [key]: value
            }
        };
        updateAXNUIThemePropertyValue(newPropertyValue);
    };


    const handleBackgroundColorChange = (newValue: string) => {
        updateAXNUIThemeGeneralFieldValue("innerBackgroundColor", newValue);
    };

    const handleResetBackgroundColor = () => {
        updateAXNUIThemeGeneralFieldValue("innerBackgroundColor", inheritedAXNUITheme.general.innerBackgroundColor);
    };

    const handleFormPanelDescriptionChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormPanelFieldValue("descriptionText", event.target.value);
    };

    const handleResetFormPanelDescription = () => {
        updateAXNUIContentFormPanelFieldValue("descriptionText", inheritedAXNUIContent.formPanel.descriptionText);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.FORM_PANEL);
    }, [generatePreviewComponent]);

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.background.title}>
                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={inheritedAXNUITheme.general.innerBackgroundColor}
                                currentThemingPropertyValue={axnuiTheme.general.innerBackgroundColor}
                                pageLevel={pageLevel}
                            >
                                <ColorPickerFormGroup
                                    controlId={"theming-form-panel-background-color-picker"}
                                    automationId={"theming-form-panel-background-color-picker"}
                                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.background.inputs.backgroundColor.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.background.inputs.backgroundColor.label}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    value={axnuiTheme.general.innerBackgroundColor || ""}
                                    onChange={handleBackgroundColorChange}
                                    position={"bottom"}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.background.inputs.backgroundColor.tooltip}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetBackgroundColor}
                                />
                            </ThemingFormGroupWrapper>
                        </ThemingFormSection>
                        <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.title}>
                            <ThemingFormPanelTextSection
                                axnuiContent={axnuiContent}
                                axnuiLayout={axnuiLayout}
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUIContent={inheritedAXNUIContent}
                                inheritedAXNUILayout={inheritedAXNUILayout}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateAXNUIContentFormPanelFieldValue={updateAXNUIContentFormPanelFieldValue}
                                updateAXNUILayoutFormPanelFieldValue={updateAXNUILayoutFormPanelFieldValue}
                                updateAXNUIThemeFormPanelFieldValue={updateAXNUIThemeFormPanelFieldValue}
                            />
                        </ThemingFormSection>
                        <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelLogo.title}>
                            <ThemingFormPanelLogoSection
                                axnuiContent={axnuiContent}
                                axnuiLayout={axnuiLayout}
                                inheritedAXNUIContent={inheritedAXNUIContent}
                                inheritedAXNUILayout={inheritedAXNUILayout}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                uploadFileToS3={uploadFileToS3}
                                updateAXNUIContentFormPanelFieldValue={updateAXNUIContentFormPanelFieldValue}
                                updateAXNUILayoutFormPanelFieldValue={updateAXNUILayoutFormPanelFieldValue}
                            />
                        </ThemingFormSection>
                        <ThemingFormSection showSeparator={false} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelDescription.title}>
                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={inheritedAXNUIContent.formPanel.descriptionText}
                                currentThemingPropertyValue={axnuiContent.formPanel.descriptionText}
                                pageLevel={pageLevel}
                            >
                                <TextareaFormGroup
                                    controlId={"theming-form-panel-description-input"}
                                    automationId={"theming-description-input"}
                                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelDescription.inputs.formPanelDescriptionText.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelDescription.inputs.formPanelDescriptionText.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleFormPanelDescriptionChange}
                                    value={axnuiContent.formPanel.descriptionText || ""}
                                    disabled={isDisabled}
                                    isRequired={false}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelDescription.inputs.formPanelDescriptionText.tooltip}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetFormPanelDescription}
                                />
                            </ThemingFormGroupWrapper>
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div >
    );
};